import React, { useEffect } from 'react';
import classnames from 'classnames';
import { MixAndMatchProductPicker } from 'src/MixAndMatchBundle/MixAndMatchProductPicker';
import { calculateQuantity } from 'src/MixAndMatchBundle/utils/calculateQuantity';
import { CamelizedMediaEntry } from 'src/types/global-types';
import { camelized_BundleConfigOptions_configurable_options_values } from 'src/queries/__fragments__/__generated__/BundleConfigOptions';
import { FormikValuesBundlesType } from 'src/MixAndMatchBundle/MixAndMatchBundle';
import { useViewport } from 'src/hooks/useViewport';
import { WearerOptionMap } from 'src/components/WeareBundleProduct/utils/wearer.types';
import { WearerSummary } from 'src/components/BundleProduct/BundleOptionsProvider';

import classes from './MixAndMatchProductContainer.scss';

export type MixAndMatchVariant = {
    title: string;
    image: string;
    id: number;
    value: {
        colors: camelized_BundleConfigOptions_configurable_options_values[] | undefined;
        sizes: camelized_BundleConfigOptions_configurable_options_values[] | undefined;
    };
    mediaEntries: CamelizedMediaEntry[];
    description: { html: string };
    sizeGuides: { content: string };
};

type Props = {
    title: string;
    requiredQty: number;
    bundles: FormikValuesBundlesType[];
    setFormikValueOfBundles: (bundles: FormikValuesBundlesType[]) => void;
    validateFormikForm: () => void;
    index: number;
    variants: MixAndMatchVariant[];
    wearerOptions: WearerOptionMap;
    summary: WearerSummary[];
};

export const MixAndMatchProductContainer: React.FC<Props> = ({
    title,
    requiredQty,
    bundles,
    setFormikValueOfBundles,
    validateFormikForm,
    index,
    variants,
    wearerOptions,
    summary,
}) => {
    const currentQty = calculateQuantity(bundles[index]);
    const correctQty = currentQty === requiredQty;
    const wrongQty = currentQty > requiredQty;
    const { isMobile } = useViewport(900);

    const handleAddProductToBundle = (index: number, id: number, name: string) => {
        const productData = {
            id: id,
            name: name,
            selectedColorsAndSizes: [],
        };
        bundles[index].products.push(productData);

        setFormikValueOfBundles(bundles);
    };

    const handleRemoveProductFromBundle = (index: number, productIndex: number) => {
        bundles[index].products = bundles[index].products.filter((product, index) => index !== productIndex);

        setFormikValueOfBundles(bundles);
    };

    const handleBundleColorChange = async (
        swatch: camelized_BundleConfigOptions_configurable_options_values,
        index: number,
        productIndex: number,
        productToSwapColors?: number,
    ) => {
        const colorsAndSizes = bundles[index].products[productIndex].selectedColorsAndSizes;
        const colorAlreadySelected = colorsAndSizes?.findIndex((bundle) => bundle.colorId === swatch.valueIndex) >= 0;

        if (typeof productToSwapColors !== 'undefined') {
            const productColorData = bundles[index].products.find(
                (product) => product.id === productToSwapColors,
            )?.selectedColorsAndSizes;

            productColorData?.splice(0, productColorData.length);

            productColorData?.push({
                colorId: swatch.valueIndex,
                sizes: { id: undefined, quantity: undefined },
            });

            return validateFormikForm();
        }

        if (colorAlreadySelected) {
            bundles[index].products[productIndex].selectedColorsAndSizes = colorsAndSizes.filter(
                (bundle) => bundle.colorId !== swatch.valueIndex,
            );

            await setFormikValueOfBundles(bundles);
        } else {
            bundles[index].products[productIndex].selectedColorsAndSizes.push({
                colorId: swatch.valueIndex,
                sizes: { id: undefined, quantity: undefined },
            });
            await setFormikValueOfBundles(bundles);
        }

        return validateFormikForm();
    };

    const handleBundleSizeChange = (size: { id: number; quantity: number }, index: number, productIndex: number) => {
        bundles[index].products[productIndex].selectedColorsAndSizes[0].sizes = size;

        setFormikValueOfBundles(bundles);
    };

    const handleBundleQuantityChange = (quantity: number, index: number, productIndex: number) => {
        bundles[index].products[productIndex].selectedColorsAndSizes[0].sizes.quantity = quantity;

        setFormikValueOfBundles(bundles);
    };

    const internalOnChange = () => {
        if (correctQty && isMobile) {
            const elementId = bundles.length === index + 1 ? 'container-add-a-logo' : `container-${index + 1}`;
            const element = document.getElementById(elementId);
            const headerHeight = document.getElementById('header')?.offsetHeight;
            const elementPosition = element?.getBoundingClientRect().top;
            const offsetPosition = elementPosition! + window.scrollY - headerHeight!;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth',
            });
        }
    };

    useEffect(() => {
        internalOnChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [correctQty]);

    return (
        <div id={`container-${index}`} className={classes.stepAndProductContainer}>
            <h3 className={classes.title}>{`Step ${index + 1} - ${title}`}</h3>
            <div
                className={classnames(classes.container, {
                    [classes.containerForCorrectQty]: correctQty,
                })}
            >
                <div className={classes.qtyContainer}>
                    <span
                        className={classnames(classes.qty, {
                            [classes.correctQty]: correctQty,
                            [classes.wrongQty]: wrongQty,
                        })}
                    >
                        <span className={classes.currentQty}>{currentQty}</span>
                        <span className={classes.maxQty}>/{requiredQty}</span>
                    </span>
                    {wrongQty && <span className={classes.wrongQtyText}>Please remove some items</span>}
                    {correctQty && (
                        <span className={classes.correctQtyText}>
                            <img
                                className={classes.qtyCheckmark}
                                src={require('../../static/icons/GreenCheckMark.png')}
                                alt="Checkmark"
                            />
                            Done!
                        </span>
                    )}
                </div>
                <MixAndMatchProductPicker
                    handleAddProductToBundle={handleAddProductToBundle}
                    handleRemoveProductFromBundle={handleRemoveProductFromBundle}
                    handleBundleColorChange={handleBundleColorChange}
                    handleBundleSizeChange={handleBundleSizeChange}
                    index={index}
                    handleBundleQuantityChange={handleBundleQuantityChange}
                    variants={variants}
                    wearerOptions={wearerOptions}
                    summary={summary}
                    bundles={bundles}
                />
                {correctQty && (
                    <img
                        className={classes.containerCheckmark}
                        src={require('../../static/icons/MixMatchCheck.png')}
                        alt="Checkmark"
                    />
                )}
            </div>
        </div>
    );
};
